import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ScheduleRecommendations = ({ filters, reducedMotion }) => {
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchRecommendations();
  }, [filters]);

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/insights/v2/schedule-recommendations`);
      const data = await response.json();
      setRecommendations(data.recommendations || []);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyRecommendation = (rec) => {
    toast.success(`Applied recommendation: ${rec.title}`);
  };

  const getImpactColor = (impact) => {
    if (impact >= 0.2) return 'text-emerald-400';
    if (impact >= 0.1) return 'text-ink-500';
    return 'text-amber-400';
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="schedule-recommendations-card">
      <CardHeader>
        <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-violet-400" />
          AI Schedule Recommendations
        </CardTitle>
        <CardDescription className="text-slate-400 mt-1">
          ML-powered call scheduling optimizations based on historical patterns
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-800/30 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : recommendations.length > 0 ? (
          <div className="space-y-3">
            {recommendations.map((rec, index) => (
              <div
                key={index}
                className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50 hover:bg-slate-800/50 transition-all duration-200"
                style={{
                  animation: reducedMotion ? 'none' : `fadeIn 0.3s ease-out ${index * 0.05}s both`
                }}
                data-testid={`recommendation-${index}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-violet-400" />
                      <h4 className="font-medium text-slate-100" data-testid="recommendation-title">{rec.title}</h4>
                    </div>
                    <p className="text-sm text-slate-400 mb-2" data-testid="recommendation-description">{rec.description}</p>
                    <div className="flex items-center gap-4 text-xs">
                      <div>
                        <span className="text-slate-500">Expected Impact:</span>
                        <span className={`ml-2 font-semibold ${getImpactColor(rec.expected_impact)}`} data-testid="expected-impact">
                          +{(rec.expected_impact * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500">Confidence:</span>
                        <span className="ml-2 text-slate-300" data-testid="confidence">{(rec.confidence * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleApplyRecommendation(rec)}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    size="sm"
                    data-testid={`apply-recommendation-${index}`}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Apply
                  </Button>
                </div>
                <div className="pt-3 border-t border-slate-700/30">
                  <Badge className="bg-ink-500 text-ink-500 border-ink-300 dark:border-ink-700" data-testid="recommendation-type">
                    {rec.type}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-600" />
            <p className="text-slate-400">No schedule recommendations available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ScheduleRecommendations;